// Note: Install react-flow-renderer: npm install reactflow

import React, { useCallback, useMemo, useEffect } from 'react';
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  BackgroundVariant,
  useNodesState,
  useEdgesState,
  addEdge,
  type Connection,
  type Edge,
  type Node,
} from 'reactflow';
import 'reactflow/dist/style.css';
import type { Task } from '../../../services/projectManagement/tasksService';

interface DependencyGraphProps {
  tasks: Task[];
  dependencies: Array<{
    id: string;
    from_task_id: string;
    to_task_id: string;
    dependency_type: 'blocks' | 'blocked_by' | 'related_to';
  }>;
  criticalTaskIds?: string[];
  violationTaskIds?: string[];
  violationEdges?: Array<{ from_task_id: string; to_task_id: string }>;
  onDependencyAdd?: (fromTaskId: string, toTaskId: string, type: string) => void;
  onDependencyRemove?: (dependencyId: string) => void;
}

const DependencyGraph: React.FC<DependencyGraphProps> = ({
  tasks,
  dependencies,
  criticalTaskIds,
  violationTaskIds,
  violationEdges,
  onDependencyAdd,
  onDependencyRemove,
}) => {
  const criticalSet = useMemo(() => new Set(criticalTaskIds || []), [criticalTaskIds]);
  const violationSet = useMemo(() => new Set(violationTaskIds || []), [violationTaskIds]);
  const violationEdgeSet = useMemo(() => {
    const s = new Set<string>();
    (violationEdges || []).forEach(e => s.add(`${e.from_task_id}::${e.to_task_id}`));
    return s;
  }, [violationEdges]);

  // Convert tasks to nodes
  const initialNodes: Node[] = useMemo(() => {
    return tasks.map((task, index) => ({
      id: task.id,
      type: 'default',
      position: {
        x: (index % 5) * 250,
        y: Math.floor(index / 5) * 150,
      },
      data: {
        label: (
          <div className="text-sm">
            <div className="font-semibold truncate max-w-[200px]">{task.title}</div>
            <div className="text-xs text-gray-500 mt-1">
              <span className={`inline-block px-2 py-1 rounded text-xs ${
                task.status === 'done' ? 'bg-green-100 text-green-800' :
                task.status === 'in-progress' ? 'bg-blue-100 text-blue-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {task.status}
              </span>
            </div>
          </div>
        ),
      },
      style: {
        background: '#fff',
        border: violationSet.has(task.id)
          ? '2px solid #ef4444'
          : criticalSet.has(task.id)
          ? '2px solid #f59e0b'
          : '2px solid #e5e7eb',
        borderRadius: '8px',
        padding: '10px',
        width: 220,
      },
    }));
  }, [criticalSet, tasks, violationSet]);

  // Convert dependencies to edges
  const initialEdges: Edge[] = useMemo(() => {
    return dependencies.map(dep => ({
      id: dep.id,
      source: dep.from_task_id,
      target: dep.to_task_id,
      label: dep.dependency_type === 'blocks' ? '🚫 blocks' :
             dep.dependency_type === 'blocked_by' ? '⏸️ blocked by' :
             '🔗 related',
      type: 'smoothstep',
      animated: dep.dependency_type === 'blocks',
      style: {
        stroke: violationEdgeSet.has(`${dep.from_task_id}::${dep.to_task_id}`)
          ? '#ef4444'
          : dep.dependency_type === 'blocks'
          ? '#ef4444'
          : dep.dependency_type === 'blocked_by'
          ? '#f59e0b'
          : '#3b82f6',
        strokeWidth: violationEdgeSet.has(`${dep.from_task_id}::${dep.to_task_id}`) ? 3 : 1,
      },
    }));
  }, [dependencies, violationEdgeSet]);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  useEffect(() => {
    setNodes(initialNodes);
  }, [initialNodes, setNodes]);

  useEffect(() => {
    setEdges(initialEdges);
  }, [initialEdges, setEdges]);

  const onConnect = useCallback(
    (params: Connection) => {
      if (params.source && params.target) {
        // Detect cycle
        if (wouldCreateCycle(params.source, params.target, edges)) {
          alert('Cannot create dependency: This would create a circular dependency!');
          return;
        }

        setEdges((eds: Edge[]) => addEdge(params, eds));
        onDependencyAdd?.(params.source, params.target, 'blocks');
      }
    },
    [edges, onDependencyAdd, setEdges]
  );

  return (
    <div className="h-[600px] w-full rounded-lg border border-gray-200 bg-white">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onEdgeClick={(_event, edge) => {
          if (!onDependencyRemove) return;
          if (!window.confirm('Remove this dependency?')) return;
          onDependencyRemove(edge.id);
        }}
        onConnect={onConnect}
        fitView
      >
        <Controls />
        <MiniMap />
        <Background variant={BackgroundVariant.Dots} gap={12} size={1} />
      </ReactFlow>

      {/* Legend */}
      <div className="absolute bottom-20 left-4 bg-white border border-gray-200 rounded-lg p-3 shadow-lg">
        <div className="text-xs font-semibold text-gray-700 mb-2">Legend</div>
        <div className="space-y-1 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-4 h-0.5 bg-red-500"></div>
            <span>🚫 Blocks</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-0.5 bg-orange-500"></div>
            <span>⏸️ Blocked By</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-0.5 bg-blue-500"></div>
            <span>🔗 Related</span>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Check if adding an edge would create a cycle
 */
function wouldCreateCycle(
  sourceId: string,
  targetId: string,
  edges: Edge[]
): boolean {
  const visited = new Set<string>();
  const stack = [targetId];

  while (stack.length > 0) {
    const current = stack.pop()!;
    
    if (current === sourceId) {
      return true; // Cycle detected
    }

    if (visited.has(current)) {
      continue;
    }

    visited.add(current);

    // Find all edges from current node
    const outgoingEdges = edges.filter(e => e.source === current);
    outgoingEdges.forEach(edge => {
      if (edge.target) {
        stack.push(edge.target);
      }
    });
  }

  return false;
}

export default DependencyGraph;
